import qs from 'qs';

import { Order } from '@luxuryescapes/contract-svc-order';

import { request } from './common';

export type SaleOwnershipSwapStatus = Order.SaleOwnershipSwapStatus;
export type SaleOwnershipSwapReason = Order.SaleOwnershipSwapReason;
export type SaleOwnershipSwapApprovalMode = 'manual' | 'auto';
export type SaleOwnershipSwapRule =
  | 'recipient_limit'
  | 'month_end'
  | 'booking_ttv'
  | 'swap_reason'
  | 'customer_owned_sale';

/**
 * Carries both the threshold that was in force and the value actually measured, so a held
 * request stays explainable after a team leader moves the threshold.
 */
export interface SaleOwnershipSwapMatchedRule {
  rule: SaleOwnershipSwapRule;
  threshold?: number | Array<SaleOwnershipSwapReason>;
  actual?: number | SaleOwnershipSwapReason;
  orderTotal?: number;
  orderCurrency?: string;
}

export interface SaleOwnershipSwapAutoApprovalEvaluation {
  outcome: 'auto_approved' | 'held';
  matchedRules: Array<SaleOwnershipSwapMatchedRule>;
  errors: Array<string>;
}

// svc-order snapshots the order's medium when a swap is raised and optionally carries a requested
// medium that is applied on approval, and records how a request was approved plus the rules that
// held it. Declared here until contract-svc-order 1.18.3 (which adds these fields plus the create
// payload's utmMedium) is published and pinned.
export type SaleOwnershipSwapRequest = Order.SaleOwnershipSwapRequest & {
  fromUtmMedium: string | null;
  toUtmMedium: string | null;
  approvalMode?: SaleOwnershipSwapApprovalMode;
  autoApprovalEvaluation?: SaleOwnershipSwapAutoApprovalEvaluation | null;
};

export type GetSaleOwnershipSwapsParams = Omit<Order.GetSaleOwnershipSwapRequestsQuery, 'limit' | 'offset'> & {
  limit?: string;
  offset?: string;
  fromUserId?: string;
  toUserId?: string;
  actionedBy?: string;
  actionedAtFrom?: string;
  actionedAtTo?: string;
  requestedAtFrom?: string;
  requestedAtTo?: string;
};

export type GetSaleOwnershipSwapsResponse = Omit<Order.SaleOwnershipSwapRequestsResponse, 'result'> & {
  result: Array<SaleOwnershipSwapRequest>;
};

export type GetAllSaleOwnershipSwapsParams = Omit<GetSaleOwnershipSwapsParams, 'limit' | 'offset'>;

export type CreateSaleOwnershipSwapPayload = Order.CreateSaleOwnershipSwapRequestBody & {
  /** Medium to apply to the order when the swap is approved. Omitted leaves the order's medium as is. */
  utmMedium?: string;
};

export type CreateSaleOwnershipSwapResponse = Omit<Order.SaleOwnershipSwapRequestResponse, 'result'> & {
  result: SaleOwnershipSwapRequest;
};

export type UpdateSaleOwnershipSwapPayload =
  | { status: 'approved' }
  | { status: 'rejected'; rejectionNote: string }
  | { status: 'cancelled' };

export type UpdateSaleOwnershipSwapResponse = Omit<Order.SaleOwnershipSwapRequestResponse, 'result'> & {
  result: SaleOwnershipSwapRequest;
};

export interface SaleOwnershipSwapReasonOption {
  value: SaleOwnershipSwapReason;
  label: string;
  requiresNote: boolean;
}

export interface GetSaleOwnershipSwapReasonsResponse {
  result: Array<SaleOwnershipSwapReasonOption>;
}

function getBaseURL() {
  return window.configs.API_HOST + '/api';
}

const basePath = () => `${getBaseURL()}/orders/ownership-swap-requests`;
const EXPORT_PAGE_SIZE = 100;
/** Caps an export at 100 sequential API requests so an unfiltered query cannot grow without bound. */
export const MAX_EXPORT_ROWS = 10_000;

export class SaleOwnershipSwapExportLimitError extends Error {
  constructor(public readonly total: number) {
    super('Too many sale ownership swaps to export.');
    this.name = 'SaleOwnershipSwapExportLimitError';
  }
}

export async function createSaleOwnershipSwap(
  payload: CreateSaleOwnershipSwapPayload,
): Promise<CreateSaleOwnershipSwapResponse> {
  return request(basePath(), {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function getSaleOwnershipSwaps(
  params: GetSaleOwnershipSwapsParams,
  signal?: AbortSignal,
): Promise<GetSaleOwnershipSwapsResponse> {
  const query = qs.stringify(params, { addQueryPrefix: true });
  return request(`${basePath()}${query}`, { signal });
}

export async function getAllSaleOwnershipSwaps(
  params: GetAllSaleOwnershipSwapsParams,
  signal?: AbortSignal,
): Promise<Array<SaleOwnershipSwapRequest>> {
  const firstPage = await getSaleOwnershipSwaps(
    {
      ...params,
      limit: EXPORT_PAGE_SIZE.toString(),
      offset: '0',
    },
    signal,
  );

  if (firstPage.total > MAX_EXPORT_ROWS) {
    throw new SaleOwnershipSwapExportLimitError(firstPage.total);
  }

  const rows = [...firstPage.result];

  for (let offset = EXPORT_PAGE_SIZE; offset < firstPage.total; offset += EXPORT_PAGE_SIZE) {
    const page = await getSaleOwnershipSwaps(
      {
        ...params,
        limit: EXPORT_PAGE_SIZE.toString(),
        offset: offset.toString(),
      },
      signal,
    );
    rows.push(...page.result);
  }

  return rows;
}

export async function getSaleOwnershipSwapReasons(signal?: AbortSignal): Promise<GetSaleOwnershipSwapReasonsResponse> {
  return request(`${basePath()}/reasons`, { signal });
}

export async function updateSaleOwnershipSwap(
  id: string,
  payload: UpdateSaleOwnershipSwapPayload,
): Promise<UpdateSaleOwnershipSwapResponse> {
  return request(`${basePath()}/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json' },
  });
}
